import { QueryClient } from '@tanstack/react-query';
import qs from 'qs';
import application from '../../config/app/config';
import IConsentDto from '../domain/dto/IConsentDto';
import ConsentStatus from '../domain/enums/ConsentStatus';
import {
  accessTokenInitializer,
  accessTokenStorage,
  bankSelectApi,
  callbackTokenScopeInitializer,
  configurationService,
  configurationStorageService,
  connectionSummaryService,
  consentInfoService,
  consentService,
  consentStorage,
  dateSource,
  paymentAccountConfirmationService,
  providerService,
  tokenDecoder,
  tokenEncoder,
  userIdStorage,
  validationTokenInitializer,
  validationTokenStorage
} from '../domain/services';
import CallbackTokenScopeEnum from '../types/enums/callbackTokenScopeEnum';
import ErrorScreenTypeEnum from '../types/enums/errorScreenTypeEnum';
import ProviderAuthStatusEnum from '../types/enums/providerAuthStatusEnum';
import RedirectionErrorTypeEnum from '../types/enums/redirectionErrorTypeEnum';
import IPaymentAccountConfirmationSummaryProps from '../types/IPaymentAccountConfirmationSummaryProps';
import { ServiceUrls } from '../types/ServiceUrls';
import { selectRedirectionErrorScreenType } from '../utils/ErrorHandlingHelpers';
import AccountNumberValidator from '../utils/validators/accountNumberValidator';
import BankCodeValidator from '../utils/validators/bankCodeValidator';
import {
  FlowType,
  useAppState,
  useCreditAccountsState,
  useErrorState,
  useSelectedBank,
  useSelectedProvider,
  useStepsState
} from './zustandHooks';
import { useHistory } from 'react-router';
import { queryKeys } from './queryHooks';
import { ISettingsDto } from '../domain/dto/ISettingsDto';
import { queryClient, trackEvent } from '../App';
import { tuBankSelectParams } from '../types/IBankSelectionProps';
import { EventTypes } from '../utils/applicationInsights';

// https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_device_detection
export const isMobile = () => {
  let hasTouchScreen = false;
  if ('maxTouchPoints' in window.navigator) {
    hasTouchScreen = window.navigator.maxTouchPoints > 0;
  } else if ('msMaxTouchPoints' in window.navigator) {
    //@ts-ignore
    hasTouchScreen = window.navigator.msMaxTouchPoints > 0;
  } else {
    const mQ = matchMedia?.('(pointer:coarse)');
    if (mQ?.media === '(pointer:coarse)') {
      hasTouchScreen = !!mQ.matches;
    } else if ('orientation' in window) {
      hasTouchScreen = true; // deprecated, but good fallback
    } else {
      // Only as a last resort, fall back to user agent sniffing
      //@ts-ignore
      const UA = window.navigator.userAgent;
      hasTouchScreen =
        /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) || /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA);
    }
  }
  return hasTouchScreen;
};

export const connectionSummaryFn = async (url: string, token: string) => {
  const summary = await connectionSummaryService.getConnectionSummary(url, token);

  if (!userIdStorage.hasUserId() && summary?.userId) {
    userIdStorage.setUserId(summary.userId);
  }
  return summary;
};

const providerPreSelectFn = async (serviceUrls: ServiceUrls) => {
  const { providerId, clientId, configurationId } = useAppState.getState().appState;
  const setSelectedBank = useSelectedBank.getState().setSelectedBank;
  const setSelectedProvider = useSelectedProvider.getState().setSelectedProvider;

  if (!providerId || !serviceUrls?.providerSearchService) return;
  const bank = await providerService.getBank(serviceUrls.providerSearchService, providerId, clientId, configurationId);

  if (!bank.name) throw Error();

  setSelectedBank({
    ...bank,
    providers: bank.providers.map(provider => ({
      providerId: provider.providerId,
      name: provider.name,
      maxConsentDays: provider.maxConsentDays,
      dataSource: provider.dataSource,
      hasDisplayName: false,
      supportedAccounts: { Business: [], Personal: [] }
    }))
  });

  const selectedProvider = bank.providers.find(provider => provider.providerId === providerId);
  if (selectedProvider === undefined)
    throw Error(`providerId[${providerId}] could not be found in selected bank[${bank.name}].`);
  setSelectedProvider(selectedProvider);
};

const bankCodePreSelect = async (serviceUrls: ServiceUrls) => {
  const { bankCode, countryCode } = useAppState.getState().appState;
  const setSelectedBank = useSelectedBank.getState().setSelectedBank;
  const setSelectedProvider = useSelectedProvider.getState().setSelectedProvider;
  const banks = await providerService.getBanks(serviceUrls.providerSearchService!, bankCode!, countryCode!);

  if (!banks || banks?.length < 1) throw Error('no bank found.');
  if (banks.length > 1) throw Error('Expected one bank only to be present.');

  const bank = banks.first();

  if (!bank?.name) throw Error('Bank name missing');
  if (bank?.providers?.length > 1) throw Error('Expected one provider only to be present.');

  const firstProvider = bank.providers.first();
  setSelectedProvider(firstProvider);
  setSelectedBank({
    ...bank,
    providers: bank.providers.map(provider => ({
      providerId: provider.providerId,
      name: provider.name,
      maxConsentDays: provider.maxConsentDays,
      dataSource: provider.dataSource,
      hasDisplayName: false,
      supportedAccounts: { Business: [], Personal: [] }
    }))
  });
};

export const consentFn = async (serviceUrls?: ServiceUrls) => {
  const { appState, setAppState } = useAppState.getState();
  const { consentId, customerReference, clientId, configurationId } = appState;
  const setSelectedProvider = useSelectedProvider.getState().setSelectedProvider;
  const setSelectedBank = useSelectedBank.getState().setSelectedBank;

  // getConsent is called in reconfirmation and reauthentication.
  const consent = await consentService.getConsent(
    serviceUrls?.consentService!,
    consentId!,
    accessTokenStorage.getToken()!
  );

  setAppState({
    ...(!clientId && { clientId: consent.applicationId }),
    ...(!customerReference?.trim() && { customerReference: consent.customerReference }),
    ...(!configurationId && { configurationId: consent.configurationName })
  });

  if (!userIdStorage.hasUserId()) {
    userIdStorage.setUserId(consent.userId);
  }

  const bank = await providerService.getBank(
    serviceUrls?.providerSearchService!,
    consent.providerId,
    consent.applicationId,
    consent.configurationName
  );
  const selectedProvider = bank.providers.find(p => p.providerId === consent.providerId);

  if (!bank.name) throw Error();

  const selectedBank = {
    ...bank,
    providers: bank.providers.map(provider => ({
      providerId: provider.providerId,
      name: provider.name,
      dataSource: provider.dataSource,
      maxConsentDays: provider.maxConsentDays,
      hasDisplayName: false,
      supportedAccounts: { Business: [], Personal: [] }
    }))
  };

  let existingConsentEndDate: Date | undefined = undefined;

  if (selectedProvider?.maxConsentDays) {
    existingConsentEndDate = new Date(consent.consentStart);
    existingConsentEndDate.setUTCDate(existingConsentEndDate.getUTCDate() + selectedProvider.maxConsentDays);
  }

  consent.consentEnd = existingConsentEndDate;

  setSelectedBank(selectedBank);
  if (selectedProvider) setSelectedProvider(selectedProvider);

  return { consent, bank: selectedBank };
};

export const consentInfoFn = async (serviceUrls?: ServiceUrls) => {
  const { appState, setAppState } = useAppState.getState();
  const keyParts = application.env.GATEWAY_SERVICE_TOKEN_VALIDATION_KEY.split(':');
  const callBackAccessToken = await tokenEncoder.Encode(keyParts[0], keyParts[1], {
    consent_id: appState?.consentId,
    application_id: appState?.clientId,
    callback_token_scope: CallbackTokenScopeEnum.Aisp
  });
  const consentInfo = await consentInfoService.getConsentInfo(serviceUrls?.gatewayService!, callBackAccessToken!);

  const statuses = [ConsentStatus.Expired, ConsentStatus.Revoked];
  setAppState({ reAuthentication: statuses.includes(consentInfo.consentStatus) });

  return consentInfo;
};

export const consentConfigurationFn = async (consent?: IConsentDto) => {
  let oldConsentEndDate: Date | undefined = undefined;
  let consentEndDate: Date | undefined = undefined;
  let consentStatus = ConsentStatus.Pending;

  const { clientId, language, configurationId, consentId } = useAppState.getState().appState;
  const selectedProvider = useSelectedProvider.getState().selectedProvider;

  const consentConfiguration = await configurationService.getTerms(clientId, language, configurationId);
  let daysOfHistoricalTransactions = consentConfiguration?.daysOfHistoricalTransactions;
  let permissionClusters = consentConfiguration?.permissionClusters;

  if (consentConfiguration?.consentEnd) consentEndDate = new Date(consentConfiguration.consentEnd);

  if (selectedProvider?.maxConsentDays) {
    let providerEndDate = dateSource.DateNow();
    providerEndDate.setUTCDate(providerEndDate.getUTCDate() + selectedProvider.maxConsentDays);

    if (!consentEndDate || providerEndDate < consentEndDate) consentEndDate = providerEndDate;
  }
  if (consentId && consent) {
    daysOfHistoricalTransactions = consent.daysOfHistoricalTransactions;
    permissionClusters = consent.permissions || [];
    consentStatus = consent.consentStatus;
    if (consent?.consentEnd) {
      oldConsentEndDate = new Date(consent?.consentEnd);
      consentStorage.setConsentEnd({
        consentEnd: consentEndDate!.toUTCString(),
        oldConsentEnd: oldConsentEndDate!.toUTCString()
      });
    }
  }

  const terms =
    consentConfiguration?.terms && consentConfiguration?.terms?.length > 0
      ? consentConfiguration?.terms.first()
      : undefined;

  trackEvent({ type: EventTypes.GET_TERMS_SUCCESS });

  return {
    terms,
    isImplicit: consentConfiguration?.isImplicit,
    isOneTimeAccess: consentConfiguration?.isOneTimeAccess,
    consentEndDate,
    oldConsentEndDate,
    consentStatus,
    daysOfHistoricalTransactions,
    permissionClusters
  };
};

export const paymentAccountConfirmationSummaryFn = async (serviceUrls?: ServiceUrls) => {
  const token = accessTokenStorage.getToken();

  const data = (await paymentAccountConfirmationService.getPaymentAccountConfirmationSummaryData(
    serviceUrls?.gatewayService!,
    token!
  )) as IPaymentAccountConfirmationSummaryProps;

  if (!data) return undefined;

  // validate bank data...
  const range = data?.accounts.length || 0;
  for (var i = 0; i < range; i++) {
    let isValidAccountNumber = AccountNumberValidator.bankDataIsValid(data?.accounts[i].accountNumber);
    let isValidBankCode = BankCodeValidator.bankDataIsValid(data?.accounts[i].bankCode!, data?.countryCode!);

    data.accounts[i].accountNumberFormFieldValue = {
      isValid: isValidAccountNumber,
      userValue: null,
      validationError: null
    };
    data.accounts[i].bankCodeFormFieldValue = {
      isValid: isValidBankCode,
      userValue: null,
      validationError: null
    };

    data.accounts[i].accountNumberIsRequired = !isValidAccountNumber;
    data.accounts[i].bankCodeIsRequired = !isValidBankCode;
  }

  return data;
};

export const completeConnection = () => {
  const { redirectUrl: redirect_url, clientId, consentId, customerReference, state } = useAppState.getState().appState;
  const settings = queryClient.getQueryData([queryKeys.settings]) as ISettingsDto;
  let redirectUrl = redirect_url || settings?.redirectUrl;
  validationTokenStorage.removeValidationToken();
  consentStorage.removeConsentEnd();
  configurationStorageService.removeItem(clientId);

  if (redirectUrl && (!redirectUrl?.includes('consent_id') || !redirectUrl?.includes('customer_ref'))) {
    redirectUrl = appendQueryParamsToRedirectUri({
      redirectUri: redirectUrl,
      ...(!redirectUrl?.includes('customer_ref') && customerReference ? { customerReference } : {}),
      ...(!redirectUrl?.includes('consent_id') && consentId ? { consentId } : {}),
      ...(!redirectUrl?.includes('state') && state?.ProviderAuthStatus ? { status: state?.ProviderAuthStatus! } : {})
    });
  }

  if (redirectUrl) {
    window.location.assign(redirectUrl);
  } else if (window.opener != null) {
    window.close();
  }
};

export const exitApp = (redirectUrl?: string) => {
  const { clientId, customerReference } = useAppState.getState().appState;
  const settings = queryClient.getQueryData([queryKeys.settings]) as ISettingsDto;
  const redirection = redirectUrl || settings?.redirectUrl;
  validationTokenStorage.removeValidationToken();
  consentStorage.removeConsentEnd();
  configurationStorageService.removeItem(clientId);

  window.hj?.('tagRecording', ['Screen: Exit Application']);

  trackEvent({ type: EventTypes.EXIT_APPLICATION });

  if (redirection) {
    window.location.assign(
      `${redirection}?error=customer_cancelled&state=error&customer_ref=${encodeURIComponent(customerReference)}`
    );
  } else if (window.opener != null) {
    window.close();
  }
};

export const initApplication = (history: ReturnType<typeof useHistory>) => {
  const { appState, setAppState } = useAppState.getState();
  const setError = useErrorState.getState().setError;

  if (!appState.state && !(appState.clientId && appState.customerReference) && !appState.consentId) {
    trackEvent({ type: EventTypes.INIT_STATE_ERROR });
    setError({
      type: ErrorScreenTypeEnum.LoadingError,
      code: 'parameters_missing'
    });
    history.push('/error');
    return;
  }

  if (window.hj) {
    if (appState.providerId) {
      window.hj('identify', appState.clientId, {
        providerId: appState.providerId
      });
    }

    if (window.opener != null) {
      window.hj('tagRecording', ['Pop-up']);
    }
  }

  const callbackTokenScope = callbackTokenScopeInitializer.init();
  if (callbackTokenScope) accessTokenStorage.setCallbackTokenScope(callbackTokenScope);

  const accessToken = accessTokenInitializer.init();
  if (accessToken) {
    accessTokenStorage.setToken(accessToken);
    let clientId = tokenDecoder.Decode(accessToken)?.appid;
    if (clientId) setAppState({ clientId });
    if (appState.consentId) setAppState({ disableCreditInformationFlow: true });
  }

  if (validationTokenStorage.hasValidationToken()) {
    setAppState({ useValidationToken: true });
  } else {
    const validationToken = validationTokenInitializer.init();
    if (validationToken) {
      validationTokenStorage.setValidationToken(validationToken);
      setAppState({ useValidationToken: true });
    }
  }
};

export const initQueries = async (
  queryClient: QueryClient,
  serviceUrls: ServiceUrls,
  history: ReturnType<typeof useHistory>,
  settings: ISettingsDto
) => {
  const { appState, setAppState } = useAppState.getState();
  const setFlow = useStepsState.getState().setFlow;
  const setError = useErrorState.getState().setError;
  const isCisFlowEnabled = settings?.enableCreditInformationFlow;
  let url, historyState;
  let flow: FlowType | undefined;

  if (appState.state) {
    url = appState.state?.ProviderAuthStatus === ProviderAuthStatusEnum.Success ? '/end-success' : '/end-error';

    if (appState.reAuthentication) {
      if (appState.error) {
        setAppState({ hideExitButton: true });
        setError({
          type: ErrorScreenTypeEnum.ReAuthenticateCancelledError,
          code: appState.error
        });
        history.push('/error');
        return;
      }
    }

    if (accessTokenStorage.hasToken() && appState.state?.ProviderAuthStatus === ProviderAuthStatusEnum.Success) {
      setAppState({ hideExitButton: true });

      if (accessTokenStorage.hasCallbackTokenScope(CallbackTokenScopeEnum.Aisp)) {
        const token = accessTokenStorage.getToken();
        await queryClient.fetchQuery({
          queryKey: [queryKeys.connectionSummary],
          queryFn: async () => await connectionSummaryFn(serviceUrls?.gatewayService!, token!)
        });

        url = isCisFlowEnabled ? '/tu-end-success' : '/end-success';
        if (isCisFlowEnabled) {
          useCreditAccountsState.setState(prev => ({ ...prev, state: { ...prev.state, isTuConnectionSuccess: true } }));
        }
      } else if (accessTokenStorage.hasCallbackTokenScope(CallbackTokenScopeEnum.PaymentConfirmation)) {
        const token = accessTokenStorage.getToken();
        await queryClient.fetchQuery({
          queryKey: [queryKeys.paymentAccountConfirmationSummary],
          queryFn: async () => {
            return await paymentAccountConfirmationService.getPaymentAccountConfirmationSummaryData(
              serviceUrls?.gatewayService!,
              token!
            );
          }
        });

        url = '/payment-account-confirmation-summary';
      }
    }

    if (appState.error) {
      const errType = selectRedirectionErrorScreenType(appState.error as RedirectionErrorTypeEnum);
      setError({ type: errType, code: appState.error });
      history.push('/error');
      return;
    }
  } else if (appState.consentId) {
    const consent = await queryClient.fetchQuery({
      queryKey: [queryKeys.consent],
      queryFn: async () => await consentFn(serviceUrls) // provider_select, bank_select done here
    });

    // onError go to /consent
    const consentInfo = await queryClient.fetchQuery({
      queryKey: [queryKeys.consentInfo],
      retry: false,
      queryFn: async () => await consentInfoFn(serviceUrls)
    });

    const excludedProviders = application.env.RECONFIRMATION_EXCLUDED_PROVIDERS?.split(',');
    const allowedStatuses = [ConsentStatus.Active, ConsentStatus.Revoked, ConsentStatus.Expired];
    if (
      consentInfo &&
      consentInfo?.countryCode === 'GB' &&
      allowedStatuses.includes(consentInfo?.consentStatus!) &&
      !excludedProviders?.includes(consentInfo?.providerId.toString())
    ) {
      setAppState({ isReconfirmation: true, hideExitButton: true });
      url = '/reconfirmation';
      flow = 'ReconfirmationFlow';
    } else if (consent) {
      url = '/consent';
      flow = 'ReauthenticationFlow';
    }
  } else if (appState.providerId) {
    await queryClient.fetchQuery({
      queryKey: [queryKeys.providerPreSelection],
      queryFn: async () => await providerPreSelectFn(serviceUrls)
    });
    trackEvent({ type: EventTypes.PROVIDER_PRESELECTION, payload: { providerId: appState.providerId } });
    url = '/consent';
  } else if (appState.bankCode && appState.countryCode) {
    await queryClient.fetchQuery({
      queryKey: [queryKeys.bankCodePreSelect],
      queryFn: async () => await bankCodePreSelect(serviceUrls)
    });
    trackEvent({
      type: EventTypes.BANK_CODE_PRESELECTION,
      payload: { bankCode: appState.bankCode, countryCode: appState.countryCode }
    });
    url = '/consent';
  }
  if (!flow) {
    flow = 'OpenBankingFlow';
    if (isCisFlowEnabled) flow = 'CreditInformationFlow';
    if (appState.documentUpload) flow = 'DocumentUploadFlow';
  }

  setFlow(flow);

  setAppState({ hideExitButton: true });

  if (url) history.push(url, historyState);
  else history.push('/welcome-screen');
};

export const tuBankSelectFnWithProviderId = async ({
  attoProviderId,
  clientId,
  configurationId
}: tuBankSelectParams) => {
  const serviceUrls = queryClient.getQueryData([queryKeys.serviceUrls]) as ServiceUrls;

  return await queryClient.fetchQuery({
    queryKey: [queryKeys.tuBankSearch, attoProviderId],
    queryFn: async ({ signal }) => {
      if (attoProviderId) {
        const bank = await providerService.getBank(
          serviceUrls?.providerSearchService!,
          Number(attoProviderId),
          clientId,
          configurationId,
          signal
        );
        return bank;
      }
    }
  });
};

export const tuBankSelectFuWithBrandName = async ({ attoBrandName, clientId, configurationId }: tuBankSelectParams) => {
  const serviceUrls = queryClient.getQueryData([queryKeys.serviceUrls]) as ServiceUrls;
  const countryCode = 'GB';
  return await queryClient.fetchQuery({
    queryKey: [attoBrandName, countryCode],
    queryFn: async ({ signal }) => {
      const banks = await bankSelectApi.getBanks(
        serviceUrls?.providerSearchService!,
        clientId,
        configurationId,
        { query: attoBrandName! },
        countryCode,
        signal
      );
      return banks;
    }
  });
};

type IAppendQueryParamsToRedirectUriParams = {
  redirectUri: string;
  status?: ProviderAuthStatusEnum;
  customerReference?: string;
  consentId?: string;
  error?: string;
};
export function appendQueryParamsToRedirectUri({
  consentId,
  customerReference,
  error,
  redirectUri,
  status
}: IAppendQueryParamsToRedirectUriParams) {
  const url = new URL(redirectUri);

  const queryParameters = qs.parse(url.search, {
    ignoreQueryPrefix: true,
    decoder: decodeURIComponent
  });

  queryParameters['state'] = status?.toLowerCase();
  queryParameters['customer_ref'] = customerReference;
  if (error != null) {
    queryParameters['error'] = error;
  }
  if (status?.toLocaleLowerCase() === 'success') {
    queryParameters['consent_id'] = consentId;
  }
  url.search = qs.stringify(queryParameters);

  return url.toString();
}
